/**
 * Usage meter.
 *
 * Hand-rolled rather than pulling in @radix-ui/react-progress — this is a filled
 * rectangle, and the dependency would buy nothing.
 *
 * Two Tailwind constraints shape the implementation: the fill width is an inline
 * style (JIT cannot generate arbitrary runtime widths like w-[73%]), and the tone
 * colours come from a static literal map (interpolated class names such as
 * `bg-${tone}-500` are never generated).
 */
import React from "react";

export type UsageTone = "neutral" | "warning" | "severe" | "danger";

export function usageTone(percent: number): UsageTone {
  if (percent >= 100) return "danger";
  if (percent >= 90) return "severe";
  if (percent >= 80) return "warning";
  return "neutral";
}

const TONE_BG: Record<UsageTone, string> = {
  neutral: "bg-blue-600",
  warning: "bg-amber-500",
  severe: "bg-orange-500",
  danger: "bg-red-600",
};

export const TONE_TEXT: Record<UsageTone, string> = {
  neutral: "text-gray-600",
  warning: "text-amber-700",
  severe: "text-orange-700",
  danger: "text-red-700",
};

interface ProgressProps {
  value: number;
  /** Pass null for an unlimited allowance — renders a full, neutral bar. */
  max: number | null;
  tone?: UsageTone | "auto";
  size?: "sm" | "md";
  label?: string;
  valueText?: string;
  showValue?: boolean;
  className?: string;
}

const Progress: React.FC<ProgressProps> = ({
  value,
  max,
  tone = "auto",
  size = "md",
  label,
  valueText,
  showValue = false,
  className = "",
}) => {
  const unlimited = max === null || max < 0;
  // Clamp the WIDTH only — the raw ratio still drives the tone, so 105% used
  // reads as danger rather than silently looking like a full-but-fine bar.
  const rawPercent = unlimited || max === 0 ? 0 : (value / max) * 100;
  const width = unlimited ? 100 : Math.min(100, Math.max(0, rawPercent));
  const resolvedTone: UsageTone = unlimited ? "neutral" : tone === "auto" ? usageTone(rawPercent) : tone;

  return (
    <div className={className}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={unlimited ? 100 : max}
        aria-valuenow={unlimited ? 100 : Math.min(value, max)}
        aria-valuetext={valueText}
        aria-label={label}
        className={`relative w-full overflow-hidden rounded-full bg-gray-200 ${
          size === "sm" ? "h-1.5" : "h-2.5"
        }`}
      >
        <div
          style={{ width: `${width}%` }}
          className={`h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none ${TONE_BG[resolvedTone]}`}
        />
      </div>
      {showValue && valueText && (
        <p className={`mt-1.5 text-xs ${TONE_TEXT[resolvedTone]}`}>{valueText}</p>
      )}
    </div>
  );
};

export default Progress;
export { Progress };
